import {ApiError, Fetcher} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {ConfigUtil} from '@mintdata-wsp/common/util/ConfigUtil';
import {IIllustration1Filters, Illustration1, Illustration1Category} from '@mintdata/mint-kit/api/assets/Illustration1';
import {IPromiseCancelable, PromiseUtil} from '@mintdata/mint-kit/impl/util/PromiseUtil';
import bind from 'bind-decorator';

interface IllustrationsSnapshot
{
    illustrations:IllustrationSnapshot[],
    stylingFillAttrValue:string;
}

export interface IllustrationSnapshot
{
    name:string;
    label:string;
    categories:string[];
    svg:string;
}

export class Illustrations1Controller
{
    private _fetcher:Fetcher;

    private _illustrations:Illustration1[] = [];

    private _illustrationsMap:Map<string, Illustration1> = new Map();

    public _illustrationsLoaded:boolean = false;

    private _illustrationLoadingPromises:Map<string, Promise<any>> = new Map();

    private _illustrationsLoadingPromise:Promise<any> = null;

    public constructor()
    {
        this._fetcher = new Fetcher();
    }

    @bind
    public getIllustrations():IPromiseCancelable<Illustration1[]>
    {
        return PromiseUtil.makeCancelable(this._getIllustrations());
    }

    @bind
    public searchIllustrations(text:string,
                               filters?:IIllustration1Filters,
                               illustrations?:Illustration1[]):IPromiseCancelable<Illustration1[]>
    {
        return PromiseUtil.makeCancelable(this._searchIllustrations(text, filters, illustrations));
    }

    @bind
    public getIllustration(name:string):IPromiseCancelable<Illustration1>
    {
        return PromiseUtil.makeCancelable(this._getIllustration(name));
    }

    @bind
    private async _getIllustrations():Promise<Illustration1[]>
    {
        if( this._illustrationsLoaded )
            return this._illustrations;

        if( !this._illustrationsLoadingPromise )
            this._illustrationsLoadingPromise = this._fetcher.request('get', ConfigUtil.cdnIllustrations1FileUrl(), null);

        const response:IllustrationsSnapshot = await this._illustrationsLoadingPromise;

        if( response instanceof ApiError )
            throw new Error(response.message);

        const illustrations:Map<string, Illustration1> = this._deserializeIllustrationsSnapshot(response);

        this._illustrations = Array.from(illustrations.values());
        this._illustrationsMap = illustrations;
        this._illustrationsLoaded = true;
        this._illustrationsLoadingPromise = null;

        return this._illustrations;
    }

    @bind
    private async _searchIllustrations(text:string, filters?:IIllustration1Filters, illustrations?:Illustration1[]):Promise<Illustration1[]>
    {
        const filterCategories:Illustration1Category[] = (filters && filters.categories) ? filters.categories : [];

        illustrations = illustrations || await this._getIllustrations();

        return illustrations.filter(({name, categories}) => (
            (
                !filterCategories.length
                || categories.some((category) => filterCategories.indexOf(category) !== -1)
            )
            && name.indexOf(text) !== -1
        ));
    }

    @bind
    private async _getIllustration(name:string):Promise<Illustration1>
    {
        if( this._illustrationsMap.has(name) )
            return this._illustrationsMap.get(name);

        const url:string = this._buildIllustrationsUrl(name);

        if( !this._illustrationLoadingPromises.has(name) )
            this._illustrationLoadingPromises.set(name, this._fetcher.request('get', url, null));

        const response:IllustrationSnapshot = await this._illustrationLoadingPromises.get(name);

        if( response instanceof ApiError )
            throw new Error(response.message);

        const illustration:Illustration1 = this._deserializeIllustrationSnapshot(response);

        this._illustrations.push(illustration);
        this._illustrationsMap.set(illustration.name, illustration);
        this._illustrationLoadingPromises.delete(name);

        return illustration;
    }

    private _buildIllustrationsUrl(name:string):string
    {
        return `${ConfigUtil.cdnIllustrations1DirUrl()}/${name}.json`;
    }

    private _deserializeIllustrationsSnapshot(snapshot:IllustrationsSnapshot):Map<string, Illustration1>
    {
        const result:Map<string, Illustration1> = new Map();

        snapshot.illustrations.forEach((illustrationSnapshot:IllustrationSnapshot) =>
        {
            const illustration:Illustration1 = this._deserializeIllustrationSnapshot(illustrationSnapshot);

            result.set(illustration.name, illustration);
        });

        return result;
    }

    private _deserializeIllustrationSnapshot(snapshot:IllustrationSnapshot):Illustration1
    {
        return new Illustration1(
            snapshot.name,
            snapshot.label,
            snapshot.categories as Illustration1Category[],
            snapshot.svg
        );
    }
}
